import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import T from '../theme';
import Icon from './Icon';
import Reveal from './Reveal';

const F = "'Plus Jakarta Sans', sans-serif";
const MONO = "'DM Mono', monospace";

export function Tag({ children, style }) {
 return (
 <span style={{
 display: 'inline-flex', alignItems: 'center', gap: 8,
 fontFamily: MONO, fontSize: '0.72rem', fontWeight: 400,
 textTransform: 'uppercase', letterSpacing: '2px',
 color: T.aquaDark, ...style,
 }}>
 <span style={{ display: 'inline-block', width: 24, height: 1.5, background: T.aqua, borderRadius: 1 }} />
 {children}
 </span>
 );
}

export function Btn({ children, variant = 'primary', onClick, style, size = 'md', type = 'button' }) {
 const [hov, setHov] = useState(false);
 const base = {
 display: 'inline-flex', alignItems: 'center', gap: 8,
 fontFamily: F, fontWeight: 700, cursor: 'pointer',
 border: 'none', borderRadius: 6, transition: 'all 0.22s ease',
 letterSpacing: '0.1px',
 padding: size === 'sm' ? '10px 22px' : size === 'lg' ? '16px 36px' : '12px 28px',
 fontSize: size === 'sm' ? '0.9rem' : size === 'lg' ? '1.05rem' : '0.95rem',
 ...style,
 };
 const variants = {
 primary: {
 background: hov ? T.btnBgHover : T.btnBg,
 color: T.btnText,
 boxShadow: hov ? '0 6px 24px rgba(110,229,158,0.45)' : '0 2px 8px rgba(110,229,158,0.3)',
 transform: hov ? 'translateY(-2px)' : 'none',
 },
 outline: {
 background: 'transparent',
 color: hov ? T.aquaDark : T.inkMid,
 border: `1.5px solid ${hov ? T.aqua : T.borderGray}`,
 transform: hov ? 'translateY(-2px)' : 'none',
 },
 ghost: {
 background: hov ? T.aquaGhost : 'transparent',
 color: T.aquaDark,
 border: `1.5px solid ${T.border}`,
 },
 outlineDark: {
 background: 'transparent',
 color: hov ? T.aqua : T.white,
 border: `1.5px solid ${hov ? T.aqua : 'rgba(255,255,255,0.35)'}`,
 transform: hov ? 'translateY(-2px)' : 'none',
 },
 };
 return (
 <button
 type={type}
 style={{ ...base, ...variants[variant] }}
 onMouseEnter={() => setHov(true)}
 onMouseLeave={() => setHov(false)}
 onClick={onClick}
 >
 {children}
 </button>
 );
}

export function SectionWrap({ children, id, style }) {
 return (
 <section id={id} style={{ padding: '100px 5%', maxWidth: 1280, margin: '0 auto', ...style }}>
 {children}
 </section>
 );
}

export function Divider() {
 return <div style={{ width: '100%', height: 1, background: T.borderGray }} />;
}

/* ── PAGE HERO — light theme, matches home banner ─────────────────────────── */
export function PageHero({ tag, title, sub }) {
 return (
 <div className="page-hero" style={{
 position: 'relative', overflow: 'hidden',
 background: T.bg,
 padding: '148px 5% 88px',
 borderBottom: `1px solid ${T.borderGray}`,
 }}>
 {/* Dot pattern */}
 <div style={{
 position: 'absolute', inset: 0, opacity: 0.55,
 backgroundImage: `radial-gradient(${T.borderGray} 1.5px, transparent 1.5px)`,
 backgroundSize: '28px 28px', pointerEvents: 'none',
 }} />
 {/* Aqua glow top-right */}
 <div style={{
 position: 'absolute', top: '-12%', right: '-6%',
 width: 520, height: 520, borderRadius: '50%',
 background: `radial-gradient(circle, ${T.aquaGhost} 0%, transparent 68%)`,
 pointerEvents: 'none',
 }} />
 {/* Navy glow bottom-left */}
 <div style={{
 position: 'absolute', bottom: '-8%', left: '-4%',
 width: 380, height: 380, borderRadius: '50%',
 background: 'radial-gradient(circle, rgba(11,31,58,0.05) 0%, transparent 68%)',
 pointerEvents: 'none',
 }} />

 <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative' }}>
 <Reveal variant="up" delay="0s">
 <Tag style={{ marginBottom: 22 }}>{tag}</Tag>
 </Reveal>
 <Reveal variant="up" delay="0.1s">
 <h1 style={{
 fontFamily: F, fontWeight: 800,
 fontSize: 'clamp(2.5rem, 5.5vw, 4.2rem)',
 letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: 22, color: T.ink,
 }}>
 {title}
 </h1>
 </Reveal>
 <Reveal variant="up" delay="0.2s">
 <p style={{ fontSize: '1.2rem', color: T.muted, maxWidth: 580, lineHeight: 1.8 }}>
 {sub}
 </p>
 </Reveal>
 </div>
 </div>
 );
}

/* ── FAQ ROW ──────────────────────────────────────────────────────────────── */
export function FAQRow({ faq }) {
 const [open, setOpen] = useState(false);
 return (
 <div style={{ borderBottom: `1px solid ${T.borderGray}` }}>
 <button
 onClick={() => setOpen(!open)}
 style={{
 width: '100%', background: 'none', border: 'none', cursor: 'pointer',
 display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 padding: '22px 0', textAlign: 'left',
 }}
 >
 <span style={{ fontFamily: F, fontWeight: 600, fontSize: '1.05rem', color: T.ink, lineHeight: 1.5 }}>
 {faq.q}
 </span>
 <span style={{
 transition: 'transform 0.25s ease',
 transform: open ? 'rotate(45deg)' : 'none',
 flexShrink: 0, marginLeft: 20,
 }}>
 <Icon name="add" size={22} color={T.aquaDark} fill={1} />
 </span>
 </button>
 {open && (
 <p style={{
 fontSize: '1rem', color: T.muted, lineHeight: 1.8,
 paddingBottom: 22, animation: 'expandDown 0.3s cubic-bezier(0.22,1,0.36,1)',
 }}>
 {faq.a}
 </p>
 )}
 </div>
 );
}

/* ── FOOTER CTA — intentionally dark for contrast ────────────────────────── */
export function FooterCTA() {
 const navigate = useNavigate();
 return (
 <div style={{
 background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 100%)`,
 padding: '100px 5%', textAlign: 'center',
 position: 'relative', overflow: 'hidden',
 }}>
 <div style={{
 position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
 width: 700, height: 700, borderRadius: '50%',
 background: 'radial-gradient(circle, rgba(10,191,173,0.1) 0%, transparent 70%)',
 pointerEvents: 'none',
 }} />
 <div style={{ position: 'relative' }}>
 <Reveal variant="up" delay="0s">
 {/* <Tag style={{ marginBottom: 18, justifyContent: 'center', color: T.aquaLight }}>
 Ready to start?
 </Tag> */}
 </Reveal>
 <Reveal variant="up" delay="0.1s">
 <h2 style={{
 fontFamily: F, fontWeight: 800,
 fontSize: 'clamp(2.2rem, 5vw, 3.6rem)',
 letterSpacing: '-1px', marginBottom: 18, lineHeight: 1.1, color: T.white,
 }}>
 Not sure what you need?<br />
 <span style={{ color: T.aqua }}>Start with a free check-in.</span>
 </h2>
 </Reveal>
 <Reveal variant="fade" delay="0.22s">
 <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.6)', marginBottom: 44, maxWidth: 500, margin: '0 auto 44px' }}>
 A 15-minute conversation. No commitment. We'll tell you exactly what's most valuable for your situation.
 </p>
 </Reveal>
 <Reveal variant="up" delay="0.32s">
 <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
 <Btn size="lg" onClick={() => navigate('/contact')}>
 Book Free 15-min Check-in →
 </Btn>
 <Btn variant="outlineDark" size="lg" onClick={() => navigate('/services')}>
 Browse Services
 </Btn>
 </div>
 </Reveal>
 </div>
 </div>
 );
}